import { CheckmarkOutline16, Edit16 } from '@carbon/icons-react';
import moment from 'moment';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { CustomerPortalButtonType } from 'api/customer-portal/theme-api';
import {
  CpBody00Secondary,
  CpBody01Primary,
  CpBody02Primary,
} from 'components/atoms/customer-portal/typography';
import { useTaskDrawerQueryParamsFromRouter } from 'hooks/use-query-params';
import { CpButton } from '../../components/cp-button';
import { useGetBackground } from '../../types/utils';
import {
  CpSectionWrapper,
  CpWidgetDescription,
  CpWidgetTitle,
} from '../common';
import { CpProjectForm, CpProjectFormsWidgetProps } from './types';

const List = styled.div`
  margin-top: var(--cp-spacing-06);
`;

const ImageAtLeftWrapper = styled.div`
  display: flex;
  gap: var(--cp-spacing-06);
  border: 1px solid var(--cp-border-subtle);
  margin-bottom: var(--cp-spacing-06);
  background-color: var(--cp-white);
  border-radius: var(--border-radius-small);

  @media (min-width: 768px) {
    gap: 0;
  }

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const IALImageWrapper = styled.div`
  @media (min-width: 768px) {
    max-width: 40%;
    width: 40%;
  }
  @media (max-width: 480px) {
    width: 100%;
  }
`;

const IALImage = styled.img`
  width: 100%;
  height: 100%;

  @media (min-width: 768px) {
    width: 100%;
    min-width: 100%;
  }
`;

const IALContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: var(--cp-spacing-07);

  @media (min-width: 768px) {
    width: 70%;
  }
`;

const DateAndAssignee = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: var(--cp-spacing-05);
`;

const FormName = styled(CpBody02Primary)`
  margin-bottom: var(--cp-spacing-03);
`;

const MFWIATLActionsWrapper = styled.div`
  display: flex;
  gap: var(--cp-spacing-05);
  margin-top: var(--cp-spacing-08);

  @media (max-width: 480px) {
    width: 100%;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    margin-top: var(--cp-spacing-05);
  }
`;

export const MultiFormWithImageAtTheLeft: React.FC<CpProjectFormsWidgetProps> =
  ({ data, disabled, widget, projectId }) => {
    const bg = useGetBackground(widget);

    const { openTaskDrawer } = useTaskDrawerQueryParamsFromRouter();

    const onSubmissionClick = useCallback(
      (form: CpProjectForm) => {
        openTaskDrawer(projectId, form.task.taskId);
      },
      [openTaskDrawer, projectId],
    );

    return (
      <CpSectionWrapper background={bg} isDisabled={disabled}>
        <CpWidgetTitle autoAdjustColor>{data.title}</CpWidgetTitle>
        {data?.description ? (
          <CpWidgetDescription autoAdjustColor>
            {data.description}
          </CpWidgetDescription>
        ) : null}
        <List>
          {data.forms.map((form) => {
            const formattedDueDate = form?.task?.dueDate
              ? moment(form.task.dueDate).format('MMM DD, YYYY')
              : '';

            return (
              <ImageAtLeftWrapper key={form.task?.taskId}>
                <IALImageWrapper>
                  <IALImage
                    src={
                      form.formTemplate?.formTheme?.headerImage ??
                      'https://s3.us-east-1.amazonaws.com/rocketlane-profile-pic-public/657/45b397c4-1d90-49af-884e-12f1788289d8/Form%20Header-12.png'
                    }
                    alt={form.formTemplate?.name}
                  />
                </IALImageWrapper>
                <IALContent>
                  <DateAndAssignee>
                    {formattedDueDate ? (
                      <CpBody00Secondary>{formattedDueDate}</CpBody00Secondary>
                    ) : (
                      <></>
                    )}
                  </DateAndAssignee>
                  <FormName>{form.task?.taskName}</FormName>
                  <CpBody01Primary>
                    {form.formTemplate?.description}
                  </CpBody01Primary>
                  <MFWIATLActionsWrapper>
                    <CpButton
                      onClick={() => onSubmissionClick(form)}
                      cpType={CustomerPortalButtonType.Primary}
                      icon={
                        !form?.hasAnswerBeenSubmitted ? (
                          <Edit16 />
                        ) : (
                          <CheckmarkOutline16 />
                        )
                      }
                      iconPlacement="left"
                    >
                      {form?.hasAnswerBeenSubmitted ? (
                        <FormattedMessage id="projects.form_responses.edit" />
                      ) : (
                        <FormattedMessage id="cp.submit_form" />
                      )}
                    </CpButton>
                  </MFWIATLActionsWrapper>
                </IALContent>
              </ImageAtLeftWrapper>
            );
          })}
        </List>
      </CpSectionWrapper>
    );
  };

const MultiFormWithAllRoundBorderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--cp-spacing-06);
  border: 1px solid var(--cp-border-subtle-auto-adjust);
  border-radius: var(--border-radius-small);
  margin-bottom: var(--cp-spacing-06);
  /* background-color: var(--cp-white); */
  border-radius: var(--border-radius-small);

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const MFWARBContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 70%;

  @media (max-width: 480px) {
    max-width: 100%;
  }
`;

const MFWARBActionsWrapper = styled.div`
  display: flex;
  gap: var(--cp-spacing-05);

  @media (min-width: 768px) {
    width: 100%;
    justify-content: flex-end;
  }

  @media (max-width: 480px) {
    width: 100%;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    margin-top: var(--cp-spacing-05);
  }
`;

export const MultiFormWithAllRoundBorder: React.FC<CpProjectFormsWidgetProps> =
  ({ data, disabled, widget, projectId }) => {
    const bg = useGetBackground(widget);
    const { openTaskDrawer } = useTaskDrawerQueryParamsFromRouter();

    const onSubmissionClick = useCallback(
      (form: CpProjectForm) => {
        openTaskDrawer(projectId, form.task.taskId);
      },
      [openTaskDrawer, projectId],
    );

    return (
      <CpSectionWrapper background={bg} isDisabled={disabled}>
        <CpWidgetTitle autoAdjustColor>{data.title}</CpWidgetTitle>
        {data?.description ? (
          <CpWidgetDescription autoAdjustColor>
            {data.description}
          </CpWidgetDescription>
        ) : null}
        <List>
          {data.forms.map((form) => {
            const formattedDueDate = form?.task?.dueDate
              ? moment(form.task.dueDate).format('MMM DD, YYYY')
              : '';

            return (
              <MultiFormWithAllRoundBorderWrapper key={form.task?.taskId}>
                <MFWARBContentWrapper>
                  <DateAndAssignee>
                    {formattedDueDate ? (
                      <CpBody00Secondary autoAdjustColor>
                        {formattedDueDate}
                      </CpBody00Secondary>
                    ) : (
                      <></>
                    )}
                  </DateAndAssignee>
                  <FormName autoAdjustColor>{form.task?.taskName}</FormName>
                  <CpBody01Primary autoAdjustColor>
                    {form.formTemplate?.description}
                  </CpBody01Primary>
                </MFWARBContentWrapper>
                <MFWARBActionsWrapper>
                  <CpButton
                    onClick={() => onSubmissionClick(form)}
                    cpType={CustomerPortalButtonType.Primary}
                    icon={
                      !form?.hasAnswerBeenSubmitted ? (
                        <Edit16 />
                      ) : (
                        <CheckmarkOutline16 />
                      )
                    }
                    iconPlacement="left"
                  >
                    {form?.hasAnswerBeenSubmitted ? (
                      <FormattedMessage id="projects.form_responses.edit" />
                    ) : (
                      <FormattedMessage id="cp.submit_form" />
                    )}
                  </CpButton>
                </MFWARBActionsWrapper>
              </MultiFormWithAllRoundBorderWrapper>
            );
          })}
        </List>
      </CpSectionWrapper>
    );
  };

const MultiFormWithBottomBorderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--cp-spacing-06);
  border-bottom: 1px solid var(--cp-border-subtle-auto-adjust);
  margin-bottom: var(--cp-spacing-06);
  /* background-color: var(--cp-white); */

  @media (max-width: 480px) {
    flex-direction: column;
    padding-left: var(--cp-spacing-05);
    padding-right: var(--cp-spacing-05);
  }
`;

const MFWBBContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 70%;

  @media (max-width: 480px) {
    max-width: 100%;
  }
`;

const MFWBBActionsWrapper = styled.div`
  display: flex;
  gap: var(--cp-spacing-05);

  @media (min-width: 768px) {
    width: 100%;
    justify-content: flex-end;
  }

  @media (max-width: 480px) {
    width: 100%;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    margin-top: var(--cp-spacing-05);
  }
`;

export const MultiFormWithBottomBorder: React.FC<CpProjectFormsWidgetProps> = ({
  data,
  disabled,
  widget,
  projectId,
}) => {
  const bg = useGetBackground(widget);
  const { openTaskDrawer } = useTaskDrawerQueryParamsFromRouter();

  const onSubmissionClick = useCallback(
    (form: CpProjectForm) => {
      openTaskDrawer(projectId, form.task.taskId);
    },
    [openTaskDrawer, projectId],
  );

  return (
    <CpSectionWrapper background={bg} isDisabled={disabled}>
      <CpWidgetTitle autoAdjustColor>{data.title}</CpWidgetTitle>
      {data?.description ? (
        <CpWidgetDescription autoAdjustColor>
          {data.description}
        </CpWidgetDescription>
      ) : null}
      <List>
        {data.forms.map((form) => {
          const formattedDueDate = form?.task?.dueDate
            ? moment(form.task.dueDate).format('MMM DD, YYYY')
            : '';

          return (
            <MultiFormWithBottomBorderWrapper key={form.task?.taskId}>
              <MFWBBContentWrapper>
                <DateAndAssignee>
                  {formattedDueDate ? (
                    <CpBody00Secondary autoAdjustColor>
                      {formattedDueDate}
                    </CpBody00Secondary>
                  ) : (
                    <></>
                  )}
                </DateAndAssignee>
                <FormName autoAdjustColor>{form.task?.taskName}</FormName>
                <CpBody01Primary autoAdjustColor>
                  {form.formTemplate?.description}
                </CpBody01Primary>
              </MFWBBContentWrapper>
              <MFWBBActionsWrapper>
                <CpButton
                  onClick={() => onSubmissionClick(form)}
                  cpType={CustomerPortalButtonType.Primary}
                  icon={
                    !form?.hasAnswerBeenSubmitted ? (
                      <Edit16 />
                    ) : (
                      <CheckmarkOutline16 />
                    )
                  }
                  iconPlacement="left"
                >
                  {form?.hasAnswerBeenSubmitted ? (
                    <FormattedMessage id="projects.form_responses.edit" />
                  ) : (
                    <FormattedMessage id="cp.submit_form" />
                  )}
                </CpButton>
              </MFWBBActionsWrapper>
            </MultiFormWithBottomBorderWrapper>
          );
        })}
      </List>
    </CpSectionWrapper>
  );
};
